import React from 'react';
import { Link } from 'react-router-dom';

const appLogo = new URL('../assets/bbyb-logo.png', import.meta.url);

const Section = ({ title, children }) => (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">{title}</h2>
        <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
);

const PrivacyPage = () => {
    const effectiveDate = 'March 19, 2026';

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#f9f4f0' }}>
            {/* Header */}
            <header className="text-white shadow-lg" style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>
                <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition">
                        <img src={appLogo.href} alt="BrickByBrickVault" className="h-7 w-auto rounded" />
                        <span className="text-lg font-bold tracking-tight">BrickByBrickVault</span>
                    </Link>
                    <Link
                        to="/"
                        className="text-sm font-medium px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition"
                    >
                        Back to app
                    </Link>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-4 py-10 sm:px-6 sm:py-14">
                <div className="text-center mb-12">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3">Privacy Policy</h1>
                    <p className="text-gray-500 text-sm">Effective date: {effectiveDate}</p>
                </div>

                <div className="space-y-6">
                    <Section title="1. About This Service">
                        <p>
                            BrickByBrickVault is a private, invitation-only file storage service operated for personal use.
                            Access is restricted to individuals who have been explicitly invited by the administrator.
                            There is no public registration or open sign-up.
                        </p>
                    </Section>

                    <Section title="2. Information We Collect">
                        <p>We collect only the minimum information necessary to operate the service:</p>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li><strong>Account information:</strong> username and email address, provided at account creation by the administrator.</li>
                            <li><strong>Uploaded files:</strong> files you choose to upload, including their names, sizes, and storage metadata.</li>
                            <li><strong>Usage data:</strong> storage usage totals, last access timestamps, and notification history associated with your account.</li>
                            <li><strong>Authentication tokens:</strong> short-lived JWT access tokens (in memory only) and hashed refresh tokens stored in the database.</li>
                        </ul>
                        <p>We do not collect analytics, advertising identifiers, or any data beyond what is required to provide the service.</p>
                    </Section>

                    <Section title="3. How We Use Your Information">
                        <p>Your information is used exclusively to:</p>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li>Authenticate you and maintain your session.</li>
                            <li>Store and retrieve files you upload.</li>
                            <li>Send transactional email notifications — account invitations and system alerts only. We do not send marketing or promotional email.</li>
                            <li>Enforce storage quotas and account policies.</li>
                        </ul>
                    </Section>

                    <Section title="4. Email Communications">
                        <p>
                            The only emails we send are transactional: an invitation email when your account is created, and
                            occasional system notifications related to your account (e.g. storage quota alerts).
                        </p>
                        <p>
                            We use Amazon Simple Email Service (AWS SES) to send these emails. All outbound email is sent over
                            TLS and includes a configuration set for bounce and complaint tracking. Hard bounces and spam complaints
                            are automatically suppressed from future sending.
                        </p>
                        <p>
                            You will not receive unsolicited email. If you receive an invitation you did not expect, you can
                            safely ignore it — it will expire automatically.
                        </p>
                    </Section>

                    <Section title="5. Data Storage and Security">
                        <p>
                            Files and account data are stored on infrastructure operated within AWS (ap-southeast-1 region).
                            All data in transit is encrypted via TLS. Data at rest is stored in Amazon S3 with server-side encryption
                            and in Amazon RDS PostgreSQL with encrypted storage.
                        </p>
                        <p>
                            Authentication tokens are short-lived. Refresh tokens are stored as SHA-256 hashes — the raw token
                            is never persisted.
                        </p>
                    </Section>

                    <Section title="6. Data Retention and Deletion">
                        <p>
                            You may request deletion of your account at any time via the Settings page. Account deletion initiates
                            a 30-day cooling period, after which all associated files and account data are permanently deleted.
                            An administrator can cancel the deletion within this period if requested.
                        </p>
                        <p>
                            Uploaded files can be deleted individually at any time from the file manager. Deletion from the
                            application removes the file from all storage locations.
                        </p>
                    </Section>

                    <Section title="7. Data Sharing">
                        <p>
                            We do not sell, rent, or share your personal data with any third parties for commercial purposes.
                            Your data is processed only by the infrastructure providers listed below as necessary to operate the service:
                        </p>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li><strong>Amazon Web Services (AWS):</strong> compute, storage, database, and email delivery.</li>
                        </ul>
                        <p>
                            We will disclose data if required by law, but will notify you where legally permitted to do so.
                        </p>
                    </Section>

                    <Section title="8. Your Rights">
                        <p>You have the right to:</p>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li>Access the data held about you (available via the Settings page).</li>
                            <li>Request correction of inaccurate data.</li>
                            <li>Request deletion of your account and all associated data.</li>
                        </ul>
                        <p>To exercise these rights, contact the administrator directly.</p>
                    </Section>

                    <Section title="9. Contact">
                        <p>
                            This service is privately operated. For any privacy-related questions or requests,
                            contact the administrator at the email address associated with your invitation.
                        </p>
                    </Section>

                    <Section title="10. Changes to This Policy">
                        <p>
                            We may update this policy occasionally. The effective date at the top of this page reflects
                            the date of the most recent revision. Continued use of the service after any changes constitutes
                            acceptance of the updated policy.
                        </p>
                    </Section>

                    <Section title="11. Terms of Service">
                        <p>
                            Use of BrickByBrickVault is also governed by the{' '}
                            <Link to="/terms" className="font-medium underline" style={{ color: '#895129' }}>
                                Terms of Service
                            </Link>
                            .
                        </p>
                    </Section>
                </div>

                <p className="text-center text-gray-400 text-xs mt-12">
                    BrickByBrickVault &middot; Private file storage &middot; {effectiveDate}
                </p>
            </main>
        </div>
    );
};

export default PrivacyPage;
